/**
 * Decks: the CoffeePie method, in any direction.
 *
 * A father taught his daughter to read at two with Anki: a few decks
 * (English, Spanish, Hebrew, math), about ten cards each a day, every deck
 * every day, the child reading aloud and a parent grading. Cards climbed a
 * natural ladder (capitals, then lowercase the next day, numbers, words,
 * words special to her, sentences, passages above her level) and reading
 * carried a second subject ("What comes after 20?", Picasso, box jellyfish).
 *
 * A deck here is that: an ordered ladder of cards on one direction the family
 * chose. It is stored as an artifact of kind `deck` (data: Deck), plays in the
 * `cards` engine, and every card is an item in her memory (`card:<deck>/<id>`)
 * so the scheduler decides which ten come back today.
 */

import { itemKey } from '../memory'
import { WORD_BY_TEXT, GRAPHEMES, SPOKEN_WORDS, WORDS } from './phonics'
import { deckTitleKey, voiceClips } from './voice'
import { NUMBER_WORDS, STARTER_DECKS } from './starterDecks'
import type { MaterialAssetRequest } from './index'

/** How a card is answered. */
export type CardKind =
  /** She reads the front aloud; a parent taps got it / not yet. The method's core. */
  | 'read'
  /** She picks the answer among a few pictures or words; self-checking. */
  | 'choose'
  /** The voice says something; she finds it. Self-checking, no reading needed. */
  | 'listen'
  /** A generated card, different every time (a pattern, a sum): Anki could not do these. */
  | 'generator'

/** What is shown or heard on one side. Any part may be missing. */
export interface CardFace {
  /** Big text: a letter, a word, a sentence, a question. */
  text?: string
  /** A word-bank word drawn from the clay set, or an asset id from the media pipeline. */
  picture?: string
  /** An emoji, when no picture is right. */
  emoji?: string
  /**
   * What the voice says: a voice-pack key (`w:cat`, `p:s`) or plain text.
   * A line the pack does not hold is recorded in the house voice as an asset
   * whose id is lineId(lang, line), so a player says `say` by playing that
   * asset when it is here and speaking the same words when it is not.
   */
  say?: string
  /**
   * What a made picture shows, when no word-bank picture fits ("a friendly
   * T. rex", "Grandma's red door"). Kept after the picture is made, so it can
   * be made again; `picture` then names the asset.
   */
  draw?: string
}

export interface Card {
  /** Stable within the deck; the memory key is `card:<deckId>/<id>`. */
  id: string
  kind: CardKind
  front: CardFace
  back?: CardFace
  /** For choose and listen: the wrong answers, as faces. */
  options?: CardFace[]
  /** For generator cards: which generator and its settings. */
  gen?: { type: 'pattern' | 'add' | 'subtract' | 'count' | 'after' | 'before' | 'rhyme'; params?: Record<string, unknown> }
}

export interface Deck {
  /** Short, stable id used in memory keys (letters, lowercase, numbers, hebrew-letters). */
  id: string
  title: string
  /** The sentence the family gave ("Hebrew letters", "dinosaurs she loves"). */
  direction: string
  /** A word-bank word or emoji for the deck's cover. */
  cover?: string
  /** The ladder: cards in the order they are taught. */
  cards: Card[]
  /** Cards a day from this deck. The method found ten to be right. */
  daily?: number
  /** Most new cards a day, before the accuracy gate. */
  maxNew?: number
  /** A deck can rest without being deleted. */
  paused?: boolean
  /**
   * Cards resting for a while, by id, each until a moment (ms): they leave
   * her daily cards until then and come back by themselves, her memory of
   * them kept. Taking a card out is for good; resting it is for now.
   */
  resting?: Record<string, number>
  /**
   * The language the cards are in, as an ISO 639-1 code ("he", "uk", "es").
   * English when absent. A deck in another language speaks through voice
   * lines made in its language alone, never an English clip of the same spelling.
   */
  lang?: string
  /** How made pictures look: clay (the house look, as the word bank), illustration or photo. */
  style?: 'clay' | 'illustration' | 'photo'
  /**
   * Card ids in groups, in teaching order: a group's cards come in as new
   * only once she knows half the group before it (stepGate), so a deck that
   * climbs (counting before adding) never runs ahead of her.
   */
  steps?: string[][]
}

/**
 * What a parent reads when a deck did not come together. One warm line,
 * whatever went wrong; the reasons stay in the server's logs.
 */
export const DECK_FAILED = 'This deck did not come together. Try again, and it usually does.'

export function cardItem(deckId: string, cardId: string): string {
  return itemKey('card', `${deckId}/${cardId}`)
}

/** The ladder as memory keys, in teaching order. */
export function deckLadder(deck: Deck): string[] {
  return deck.cards.map((c) => cardItem(deck.id, c.id))
}

/** The ids of the cards resting now (Deck.resting). */
export function restingCards(deck: Pick<Deck, 'resting'>, now: number): Set<string> {
  return new Set(Object.entries(deck.resting ?? {}).filter(([, until]) => until > now).map(([id]) => id))
}

/** The deck as it plays now: its resting cards set aside until their time. */
export function playingDeck(deck: Deck, now: number): Deck {
  const { resting: _r, ...rest } = deck
  const off = restingCards(deck, now)
  return off.size ? { ...rest, cards: deck.cards.filter((c) => !off.has(c.id)), steps: deck.steps?.map((g) => g.filter((id) => !off.has(id))).filter((g) => g.length) } : rest
}

const ID = /^[a-z0-9][a-z0-9-]{0,39}$/

/** Problems that would make a deck unplayable. Empty when it is good to save. */
export function validateDeck(d: unknown): string[] {
  const errors: string[] = []
  const deck = d as Partial<Deck> | null
  if (!deck || typeof deck !== 'object') return ['a deck must be an object']
  if (typeof deck.id !== 'string' || !ID.test(deck.id)) errors.push('id must be a short lowercase slug')
  if (typeof deck.title !== 'string' || !deck.title.trim()) errors.push('title is required')
  if (typeof deck.direction !== 'string' || !deck.direction.trim()) errors.push('direction is required')
  if (!Array.isArray(deck.cards) || deck.cards.length < 3) errors.push('a deck needs at least 3 cards')
  const seen = new Set<string>()
  ;(deck.cards ?? []).forEach((c: any, i) => {
    const at = `card ${i + 1}`
    const shape = cardShape(c, at)
    errors.push(...shape)
    if (shape.length || !c || typeof c !== 'object') return
    if (seen.has(c.id)) errors.push(`${at} repeats id ${c.id}`)
    else seen.add(c.id)
  })
  if (deck.daily !== undefined && !(Number.isInteger(deck.daily) && deck.daily >= DAILY.min && deck.daily <= DAILY.max)) errors.push(`daily must be ${DAILY.min} to ${DAILY.max}`)
  return errors
}

/** How many cards a day a deck may ask for. */
const DAILY = { min: 3, max: 20 }
const MAX_NEW = { min: 1, max: 10 }

/** One card's shape: what makes it unplayable on its own. */
function cardShape(c: any, at: string): string[] {
  if (!c || typeof c !== 'object') return [`${at} is not an object`]
  const errors: string[] = []
  if (typeof c.id !== 'string' || !ID.test(c.id)) errors.push(`${at} needs a short lowercase id`)
  if (!['read', 'choose', 'listen', 'generator'].includes(c.kind)) errors.push(`${at} has an unknown kind`)
  const face = c.front ?? {}
  if (c.kind !== 'generator' && !face.text && !face.picture && !face.emoji && !face.say && !face.draw) errors.push(`${at} has an empty front`)
  if ((c.kind === 'choose' || c.kind === 'listen') && (!Array.isArray(c.options) || c.options.length < 1)) errors.push(`${at} needs options to choose among`)
  if (c.kind === 'generator' && !c.gen?.type) errors.push(`${at} needs a generator type`)
  return errors
}

// ─────────────────────────────────────────────────────────────
// Editing a deck by hand
// ─────────────────────────────────────────────────────────────

/** A change a parent makes to a deck. The app applies it at once and the server applies the same one. */
export interface DeckEdit {
  /** A card to add, or to replace the card with the same id. */
  card?: Card
  /** Several cards to add or replace, in one edit. */
  cards?: Card[]
  /** The id of a card to take out. Her memory of it stays, under its own id. */
  remove?: string
  /** Several cards to take out, in one edit. */
  removeCards?: string[]
  /**
   * Keep only these cards, named by id or by the text on their front, and
   * take out the rest: "only sh, ch and th" keeps the sh card and the card
   * that asks her to hear sh. Applied before cards are added.
   */
  keepOnly?: string[]
  /** Cards to rest, by id or front text, until `restUntil` (ms). */
  rest?: string[]
  restUntil?: number
  /** Cards to bring back from resting now, by id or front text. */
  wake?: string[]
  paused?: boolean
  daily?: number
  /** New cards a day, at most. */
  maxNew?: number
  title?: string
  /** The line that says what the deck is. */
  direction?: string
}

/** The ids of a deck's cards named by id or by the text on their front, as a parent names them. */
export function namedCards(deck: Pick<Deck, 'cards'>, names: unknown[]): Set<string> {
  const wanted = new Set(names.map((k) => String(k).trim().toLowerCase()))
  return new Set(deck.cards.filter((c) => wanted.has(c.id.toLowerCase()) || wanted.has((c.front.text ?? '').trim().toLowerCase())).map((c) => c.id))
}

/** The cards an edit adds and the ids it takes out, whichever form it used. */
export function deckEditCards(deck: Pick<Deck, 'cards'>, edit: DeckEdit): { add: Card[]; remove: string[] } {
  const add = [...(edit.card ? [edit.card] : []), ...(Array.isArray(edit.cards) ? edit.cards : [])].filter((c) => c && typeof c === 'object')
  const named = new Set([...(typeof edit.remove === 'string' ? [edit.remove] : []), ...(Array.isArray(edit.removeCards) ? edit.removeCards : [])])
  const keep = Array.isArray(edit.keepOnly) ? namedCards(deck, edit.keepOnly) : null
  const remove = deck.cards
    .filter((c) => named.has(c.id) || (keep && !keep.has(c.id)))
    .map((c) => c.id)
  return { add, remove: [...new Set([...remove, ...[...named].filter((id) => !deck.cards.some((c) => c.id === id))])] }
}

/**
 * Apply an edit. Idempotent, because the app applies it to its copy and the
 * server applies it again to the stored one: a card with an id already in the
 * deck replaces that card in place. A new card goes to the top of the ladder,
 * so it is the next new card she meets: a parent adds "Grandma" to see it
 * tomorrow, not after the forty cards still waiting. Cards are taken out
 * before any are added, so "only these, and this new one" keeps the new one.
 *
 * It never makes a deck worse: a card that breaks the rules on its own
 * (cardProblems) is left out, a daily count is held to what a deck may ask,
 * and a removal that would leave no cards is not made. The app and the
 * server run this same function, so both keep the same deck and the server
 * has nothing to take back.
 */
export function editDeck(deck: Deck, edit: DeckEdit): Deck {
  const out: Deck = { ...deck, cards: [...deck.cards] }
  const { add, remove } = deckEditCards(deck, edit)
  const gone = new Set(remove)
  let taken: Card[] = []
  // A deck keeps at least one card: taking out the last would leave nothing to play.
  if (gone.size && out.cards.some((c) => !gone.has(c.id))) {
    taken = out.cards.filter((c) => gone.has(c.id))
    out.cards = out.cards.filter((c) => !gone.has(c.id))
  }
  for (const card of [...add].reverse()) {
    if (cardProblems(out, card).length) continue
    const i = out.cards.findIndex((c) => c.id === card.id)
    if (i >= 0) out.cards[i] = card
    else out.cards.unshift(card)
  }
  const resting = { ...(out.resting ?? {}) }
  if (Array.isArray(edit.rest) && typeof edit.restUntil === 'number' && Number.isFinite(edit.restUntil)) {
    for (const id of namedCards(out, edit.rest)) resting[id] = edit.restUntil
  }
  if (Array.isArray(edit.wake)) for (const id of namedCards(out, edit.wake)) delete resting[id]
  // A card taken out no longer rests.
  for (const id of Object.keys(resting)) if (!out.cards.some((c) => c.id === id)) delete resting[id]
  if (Object.keys(resting).length) out.resting = resting
  else delete out.resting
  if (typeof edit.paused === 'boolean') out.paused = edit.paused
  if (typeof edit.daily === 'number' && Number.isFinite(edit.daily)) out.daily = Math.min(DAILY.max, Math.max(DAILY.min, Math.round(edit.daily)))
  if (typeof edit.maxNew === 'number' && Number.isFinite(edit.maxNew)) out.maxNew = Math.min(MAX_NEW.max, Math.max(MAX_NEW.min, Math.round(edit.maxNew)))
  if (typeof edit.title === 'string' && edit.title.trim()) out.title = edit.title.trim().slice(0, 60)
  out.direction = typeof edit.direction === 'string' && edit.direction.trim() ? edit.direction.trim().slice(0, 300) : directionAfter(out, taken, cardThings(deck.cards).length)
  return out
}

/**
 * The deck's line as it stands after an edit. A line that names a card the
 * deck no longer has ("sh, ch, th, ee, oo and friends" once ee and oo are
 * out) would tell the parent it teaches what it does not, so it becomes what
 * the deck holds now: its name and the cards on its ladder. The cards gone
 * are the ones this edit took out and, for a starter's own line, any of the
 * starter's cards taken out before. A line that names none of them (the
 * family's own "dinosaurs she loves", or "Numbers 1 to 20" without 3) stays,
 * except for a count of what the deck holds ("Six sea animals" once the
 * shark is out), which becomes the count it holds now.
 */
function directionAfter(deck: Deck, taken: Card[], count: number): string {
  const escaped = (t: string) => t.replace(/[.*+?^$()|[\]{}\\]/g, '\\$&')
  // A card's word as a line names it, one or many: shark, sharks; fox, foxes.
  const word = (t: string) => new RegExp(`(^|[^\\p{L}\\p{N}])${escaped(t)}(?:e?s)?($|[^\\p{L}\\p{N}])`, 'iu')
  const things = cardThings(deck.cards)
  const has = new Set(things.map((t) => t.toLowerCase()))
  const before = STARTER_DECKS.find((d) => d.direction === deck.direction)?.cards ?? []
  const gone = cardThings([...taken, ...before]).filter((t) => !has.has(t.toLowerCase()))
  if (!gone.some((t) => word(t).test(deck.direction))) return recount(deck.direction, count, things.length)
  const shown = things.slice(0, 6)
  const list = shown.length < 2 ? shown.join('') : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
  return things.length > shown.length ? `${deck.title}: ${shown.join(', ')} and ${things.length - shown.length} more` : `${deck.title}: ${list}`
}

/**
 * What a deck's cards are about, each once, as a line would name them: the
 * word on a card's front, or for a picture card the name on its back (a
 * whale card and a "Where is the whale?" card are both the whale).
 */
function cardThings(cards: Card[]): string[] {
  const seen = new Map<string, string>()
  for (const c of cards) {
    const t = (c.front.text || c.back?.text || c.back?.say || '').trim()
    if (t && !seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t)
  }
  return [...seen.values()]
}

/** A line's count of what the deck holds ("Six sea animals", "12 words"), said again for the count it holds now, as it was written. */
function recount(line: string, before: number, after: number): string {
  if (before === after) return line
  const said = NUMBER_WORDS[before]
  // A count goes before what it counts ("Six sea animals"); a number in a range ("1 to 20") is not one.
  const re = new RegExp(`(^|[^\\p{L}\\p{N}-])(?<!\\bto\\s)(${before}${said ? `|${said}` : ''})(?=\\s+\\p{L})(?!\\s+to\\b)`, 'iu')
  return line.replace(re, (_, lead: string, n: string) => {
    if (/\d/.test(n) || !NUMBER_WORDS[after]) return `${lead}${after}`
    const w = NUMBER_WORDS[after]!
    return `${lead}${n[0] === n[0]!.toUpperCase() ? w[0]!.toUpperCase() + w.slice(1) : w}`
  })
}

/**
 * A short lowercase id from any text, unique in the deck ("Grandma" ->
 * grandma, then grandma-2). Text with no Latin letters ("бабуся", "סבתא")
 * becomes card- and a hash of the text, so the id is the same on every
 * device that adds the same words and different for different words.
 */
export function cardId(deck: Pick<Deck, 'cards'>, text: string): string {
  const base = text.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || `card-${textHash(text.trim())}`
  const taken = new Set(deck.cards.map((c) => c.id))
  let id = base
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`
  return id
}

/** Six hex characters of FNV-1a over the text: short, stable, and different for different words. */
function textHash(text: string): string {
  let h = 0x811c9dc5
  for (const ch of text) {
    h ^= ch.codePointAt(0)!
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0').slice(0, 6)
}

/**
 * The card a parent writes by hand: she reads the words, and the back shows
 * a picture (a bank word when the words are one, else one made from what the
 * parent described) and says them.
 */
export function handCard(deck: Pick<Deck, 'cards'>, input: { text: string; draw?: string; say?: string }): Card {
  const text = input.text.trim()
  const word = text.toLowerCase()
  const back: CardFace = bankPicture(word) ? { picture: word } : input.draw?.trim() ? { draw: input.draw.trim() } : {}
  if (input.say?.trim()) back.say = input.say.trim()
  return { id: cardId(deck, text), kind: 'read', front: { text }, back }
}

// ─────────────────────────────────────────────────────────────
// Media: the pictures and lines a deck needs made
// ─────────────────────────────────────────────────────────────

const PACK_KEYS = new Set(voiceClips().map((c) => c.key))

/** A line the voice pack already says in the house voice, so nothing needs making. */
export function packSays(line: string): boolean {
  const t = line.trim()
  const low = t.toLowerCase()
  return PACK_KEYS.has(t) || PACK_KEYS.has(`w:${low}`) || !!GRAPHEMES[low]
}

/** A word the clay word bank draws. */
export function bankPicture(word: string | undefined): boolean {
  return !!word && !!WORD_BY_TEXT[word.trim().toLowerCase()]
}

type Slot = 'front' | 'back' | `o${number}`

/**
 * What a deck has already made, by what it shows and says, so the same
 * subject is one picture everywhere (the T. rex she learns on one card is the
 * T. rex on the next) and the same line is recorded once.
 */
export interface Made { byDraw: Map<string, string>; lines: Set<string> }

export function madeIn(deck: Pick<Deck, 'cards' | 'lang'>): Made {
  const made: Made = { byDraw: new Map(), lines: new Set() }
  for (const c of deck.cards) {
    for (const f of [c.front, c.back, ...(c.options ?? [])]) {
      if (f?.draw && f.picture && !bankPicture(f.picture)) made.byDraw.set(f.draw.trim(), f.picture)
      if (f?.say && needsVoice(deck, f.say.trim())) made.lines.add(lineId(deck.lang, f.say.trim()))
    }
  }
  return made
}

/** Whether a line needs recording: every line in another language, and English the pack does not hold. */
function needsVoice(deck: Pick<Deck, 'lang'>, line: string): boolean {
  return !!line && ((!!deck.lang && deck.lang !== 'en') || !packSays(line))
}

/**
 * The asset id of a recorded line: its text, and in another language its
 * language first (`es:pan`), so the same words in two decks are two
 * recordings, each in its own language, when one session plays both. The
 * cards engine looks lines up the same way (sayOne).
 */
export function lineId(lang: string | undefined, line: string): string {
  return lang && lang !== 'en' ? `${lang}:${line}` : line
}

/** A recorded line, made once however many cards say it. */
function lineAsset(made: Made, lang: string | undefined, line: string): MaterialAssetRequest[] {
  const id = lineId(lang, line)
  if (made.lines.has(id)) return []
  made.lines.add(id)
  return [{ id, kind: 'voice', text: line, ...(lang && lang !== 'en' ? { lang } : {}) }]
}

/**
 * Plan one card's media: a face that asks for a new picture (`draw`) gets an
 * image asset and names it in `picture`; a line the voice pack does not hold
 * is recorded under its own text (see CardFace.say). English words the pack
 * says need nothing, so a phonics deck costs nothing to make. A read card's
 * back says the front's words when it says nothing else, because hearing the
 * answer after she reads is the reinforcement the method leans on.
 *
 * Picture ids are `<deck>.<card>.<slot>`: unique across every deck, so one
 * session can carry many decks' media, and safe as file names. Anything the
 * deck already made (`made`, by default read from the deck itself) is
 * reused, not made again. Recording a line the deck already holds costs
 * nothing: media.ts replaces an asset by id, and a line's id is its text.
 * A picture keeps its id while its words stay the same; updateDeck asks for
 * it again when the words change.
 */
export function cardMedia(
  deck: Pick<Deck, 'id' | 'lang' | 'style' | 'cards'>,
  card: Card,
  made: Made = madeIn(deck),
): { card: Card; assets: MaterialAssetRequest[] } {
  const style = deck.style ?? 'clay'
  const assets: MaterialAssetRequest[] = []
  const plan = (face: CardFace | undefined, slot: Slot): CardFace | undefined => {
    if (!face) return face
    const out: CardFace = { ...face }
    const base = `${deck.id}.${card.id}.${slot}`
    const draw = out.draw?.trim()
    if (draw && !bankPicture(out.picture)) {
      let id = made.byDraw.get(draw)
      if (!id) {
        made.byDraw.set(draw, (id = base))
        assets.push({ id, kind: 'image', prompt: draw, style, transparent: style === 'clay' })
      }
      out.picture = id
    }
    const line = out.say?.trim()
    if (line) {
      out.say = line
      if (needsVoice(deck, line)) assets.push(...lineAsset(made, deck.lang, line))
    }
    return out
  }
  // A read card with nothing said on its back says what its front says: the
  // front's own recording when it has one (a starter's w:one), its text only
  // when it has none, so nothing is said twice or recorded again.
  const back = card.kind === 'read' && (card.front.say || card.front.text) && !card.back?.say
    ? { ...card.back, say: card.front.say || card.front.text }
    : card.back
  const planned: Card = {
    ...card,
    front: plan(card.front, 'front')!,
    ...(back ? { back: plan(back, 'back') } : {}),
    ...(card.options ? { options: card.options.map((o, i) => plan(o, `o${i + 1}`)!) } : {}),
  }
  return { card: planned, assets }
}

/**
 * Every card's media, for a whole deck, each picture and line made once.
 * The title is recorded too: the player says it as the deck begins. A title
 * is always English, the parent's language in the app, whatever language
 * the cards are in (DECK_CONTRACT), so it is recorded as English: a Spanish
 * voice reading "Zoo Animals" would sound wrong to both of them.
 */
export function deckMedia(deck: Deck): { deck: Deck; assets: MaterialAssetRequest[] } {
  // Pictures a deck already names are kept; its lines are what this records.
  const made: Made = { ...madeIn(deck), lines: new Set() }
  const assets = titleVoice(deck.title)
  assets.forEach((a) => made.lines.add(a.id))
  const cards = deck.cards.map((c) => {
    const m = cardMedia(deck, c, made)
    assets.push(...m.assets)
    return m.card
  })
  return { deck: { ...deck, cards }, assets }
}

/** The recording of a deck's title, in English (see deckMedia); nothing when the pack says it. */
export function titleVoice(title: string): MaterialAssetRequest[] {
  const line = title.trim()
  // A starter's title is already in the pack (x:deck-<slug>), and the engine plays that.
  if (packSays(deckTitleKey(line))) return []
  return needsVoice({}, line) ? [{ id: lineId(undefined, line), kind: 'voice', text: line }] : []
}

/**
 * What validateDeck lets through but a written deck should not have: a
 * picture the bank cannot draw and nothing to make it from, a read card with
 * nothing to read, a generator the player does not know. Checked on every
 * generated deck and every card a parent adds, before anything is made.
 */
export function deckProblems(d: Deck): string[] {
  // Every problem at once, the deck's shape and each card's content, so the
  // one repair turn sees all of it. A card whose shape is already reported
  // by validateDeck is not read further: its content checks assume a shape.
  const errors = validateDeck(d)
  if (d.lang !== undefined && !/^[a-z]{2}$/.test(d.lang)) errors.push('lang must be a two-letter language code like "en" or "he"')
  if (d.style !== undefined && !['clay', 'illustration', 'photo'].includes(d.style)) errors.push('style must be clay, illustration or photo')
  if (d.maxNew !== undefined && !(Number.isInteger(d.maxNew) && d.maxNew >= MAX_NEW.min && d.maxNew <= MAX_NEW.max)) errors.push(`max_new must be ${MAX_NEW.min} to ${MAX_NEW.max}`)
  if (!Array.isArray(d.cards)) return errors
  if (d.cards.length > 80) errors.push('a deck holds at most 80 cards')
  d.cards.forEach((c, i) => {
    if (!cardShape(c, '').length) errors.push(...cardContent(d, c, `card ${i + 1} (${c.id})`))
  })
  return errors
}

/** A face that shows something before its made picture arrives, or if it never does. */
function showsWithoutMedia(f: CardFace | undefined): boolean {
  return !!(f && (f.text?.trim() || f.emoji?.trim() || bankPicture(f.picture)))
}

/**
 * What is wrong with one card on its own, in the deck it belongs to. A card
 * a parent adds is kept only when this is empty (editDeck).
 *
 * A made picture can be late (the deck opens while its media is made) or
 * never come (a refused prompt, a busy voice service), so a tile she must
 * tell apart from the others always carries its name or an emoji too:
 * without it the wrong answers are blank and she taps the only tile with
 * anything on it.
 */
export function cardProblems(d: Pick<Deck, 'id'>, c: Card, at = `card ${c?.id ?? ''}`): string[] {
  const shape = cardShape(c, at)
  return shape.length ? shape : cardContent(d, c, at)
}

/** What is wrong with a well-shaped card's content (see cardProblems). */
function cardContent(d: Pick<Deck, 'id'>, c: Card, at: string): string[] {
  const errors: string[] = []
  const made = `${d.id}.`
  const faces = [c.front, c.back, ...(c.options ?? [])].filter(Boolean) as CardFace[]
  for (const f of faces) {
    if (f.picture && !bankPicture(f.picture) && !f.draw?.trim() && !f.picture.startsWith(made)) {
      errors.push(`${at}: "${f.picture}" is not in the word bank; leave picture empty and describe it in draw`)
    }
    if (f.text && f.text.length > 80) errors.push(`${at}: text is too long for a card; keep it under about eight words`)
    if (f.draw && f.draw.length > 240) errors.push(`${at}: draw should be one short description`)
    if (f.emoji && [...f.emoji].length > 8) errors.push(`${at}: emoji must be a single emoji`)
  }
  const picks = c.kind === 'choose' || c.kind === 'listen'
  // A read card's front is text she reads, or a picture she names aloud
  // (a word in a language she cannot read yet); the parent grades either.
  const drawnFront = !!(c.front.draw?.trim() || (c.front.picture && !bankPicture(c.front.picture)))
  if (c.kind === 'read' && !c.front.text?.trim()) {
    if (!drawnFront && !c.front.picture && !c.front.emoji?.trim()) errors.push(`${at}: a read card needs text on the front to read, or a picture to name`)
    else if (drawnFront && !showsWithoutMedia(c.front)) errors.push(`${at}: a picture to name that is drawn needs an emoji on the front as well, shown until the picture arrives`)
  }
  if (picks && (c.options?.length ?? 0) > 3) errors.push(`${at}: at most 3 wrong options`)
  if (picks && !c.back) errors.push(`${at}: the back is the right answer; it is missing`)
  if (picks && [c.back, ...(c.options ?? [])].some((f) => f && !showsWithoutMedia(f))) {
    errors.push(`${at}: every answer and option needs text (its name) or an emoji as well as a drawn picture`)
  }
  if (c.kind === 'choose' && drawnFront && !showsWithoutMedia(c.front)) {
    errors.push(`${at}: a front with a drawn picture needs text or an emoji as well`)
  }
  if (c.kind === 'listen' && !c.front.say?.trim()) errors.push(`${at}: a listen card needs say on the front`)
  if (c.kind === 'generator' && c.gen && !GENERATORS[c.gen.type]) errors.push(`${at}: there is no generator "${c.gen.type}"; they are ${Object.keys(GENERATORS).join(', ')}`)
  return errors
}

// ─────────────────────────────────────────────────────────────
// The generator's brief
// ─────────────────────────────────────────────────────────────

/** Settings each generator reads. The engine and the brief both follow this. */
export const GENERATORS: Record<NonNullable<Card['gen']>['type'], string> = {
  pattern: 'finish the pattern: { rule: AB | AAB | ABB | ABC | AABB, set: colors | shapes | animals | letters }',
  add: 'a sum shown with things to count, answer among a few: { max: the largest total, 2 to 20; 5 then 10 }',
  subtract: 'take away, shown with things that leave: { max: the largest start, 2 to 20 }',
  count: 'count the clay things on the card: { max: 2 to 10 }',
  after: 'what number comes after n: { max: the largest n, 3 to 100; e.g. 10, then 20 }',
  before: 'what number comes before n: { max: 3 to 100 }',
  rhyme: 'the voice says a word; she picks the one that rhymes: {}',
}

/**
 * How to write a deck. The generator's system prompt is this, next to the
 * types it writes, so the two change together.
 */
export const DECK_CONTRACT = `You write one flash card deck for a young child, on one direction her family chose. It plays in Sapling's card player: a parent sits beside her, about ten cards from each deck come up a day, every deck every day, and a scheduler decides which ten from what she remembers.

The method. A father taught his daughter to read at two with Anki this way: capitals, then lowercase the next day, then numbers, then short words, then words special to her ("purple", her favourite colour), then sentences, then passages above her level. Reading carried a second subject: "What comes after 20?", Picasso, box jellyfish. Ten cards a deck a day was right; more was too many. The cards she loved were personal and a little cool: her friends' names, the animal she cannot stop talking about, a silly picture. It was never coercive; a parent stops the moment it stops being fun.

What makes a good deck:
- A ladder. Cards are listed in the order they are taught, easiest and most familiar first, each one a small step from the last. New cards come a few a day, so the first ten matter most.
- One idea per card. A letter, a word, a short question, a picture to name. Never two facts on one card.
- Personal. Use what you know about her: her name, the people and animals in her life, what she loves right now. Put some of it on cards (a card for her own name, her dog's name, "Maya likes red") when it fits the direction.
- Pitched to what she knows. Her memory is below: build on what she has, and do not re-teach what she has mastered unless the deck is a review.
- Honest content. Real facts, correctly spelled, in the right script. For another language use its real script and the words a family would actually say, with a short English gloss on the back.
- Twelve to forty cards. Enough to climb, not a textbook. A parent can always add more.

Card kinds:
- "read": the heart of it. Big text on the front that she reads aloud (a letter, a word, a short sentence or question), and the parent taps got it or not yet. The back is the reward: a picture and the voice saying it, plus an answer when the front was a question. Front text short: a letter, a word, or a sentence under about eight words. When the point is saying a word rather than reading it (a word in a language whose script she cannot read yet, naming an animal), the front is instead a picture she names aloud, with no text, and the back shows and says the word.
- "choose": she taps the answer among pictures or words; self-checking. Use it when reading is not the point (which one is the T. rex, which leaf is the oak). Front: the question, as say and as short text on the card (or a picture it asks about), so the card is never blank. Options: the 1 to 3 wrong answers, as faces; the right answer is the back.
- "listen": the voice says something and she finds it among options. Front: say is what the voice asks; back: the right answer; options: 1 to 3 wrong ones.
- "generator": a card made fresh every time, which paper flash cards cannot do. Only when the direction wants it (math, patterns, rhymes). Types and their params:
${Object.entries(GENERATORS).map(([k, v]) => `  - ${k}: ${v}`).join('\n')}

Faces. Each side is a face: text (what is shown big), say (what the voice says), picture, draw, emoji.
- picture: a word from the clay word bank below, drawn instantly for free. Prefer these whenever one fits.
- draw: when no bank picture fits, describe the picture to make, plainly and concretely, one subject ("a friendly green T. rex, standing", "an oak leaf"). It is made in the deck's style. No text, letters or numbers in pictures. Write the same words every time the same thing appears: identical draw text is one picture, so the T. rex she learns on one card is the T. rex on the next.
- emoji: only when neither fits and a symbol is right (a flag, a heart).
- say: the words the voice speaks, written exactly as they should sound. For English words in the voice pack it is instant; anything else is recorded in the same warm voice. In another language, write say in that language.
Keep one look inside a card: in a clay deck, bank pictures and drawn ones sit together; in an illustration or photo deck, draw the options too rather than mixing in clay bank pictures.
Every card needs a way to hear it: say on the back of read cards (or it says the front's text), say on the front of choose and listen cards.
A drawn picture is made after the deck is written and can be late or fail, so on choose and listen cards every answer and option (and a choose front with a drawn picture) also carries its name as text or a fitting emoji, and a picture to name on a read card carries a fitting emoji. The player shows it only while the picture is missing, so it never gives the answer away.

Word bank (clay pictures and recorded words): ${WORDS.map((w) => w.word).join(' ')}.
Other words with a recorded voice: ${SPOKEN_WORDS.join(' ')}.

The deck: id (a short lowercase slug, e.g. hebrew-letters, dinosaurs), title (two to four words in English, what a parent would call it, even when the cards are in another language: "Hebrew letters", "Food in Ukrainian"; the voice says it in English as the deck begins), direction (one short line for the parent on the deck's tile, the way the app's own decks read: what the cards are and what they build; the family's request is kept on its own), cover (a bank word or one emoji), lang (ISO 639-1 code of the card text; "en" for English), style (clay for most things; illustration for people, places and scenes; photo only for the real natural world: leaves, birds, planets), daily (cards a day, 10 unless the direction asks otherwise), max_new (new cards a day: 2 to 4 for a toddler), cards. Card ids are short lowercase slugs unique in the deck (a, b, t-rex, after-20).`
